import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Search } from 'lucide-react';
import api from '../../services/api';
import DetailPanel from '../SlidingPanel/DetailPanel';
import ApprovalDetailPage from './ApprovalDetailPage';
import commonStyles from '../../styles/common.module.css';
import styles from './ApprovalPage.module.css';
import { formatDateTime } from '../../utils/dateUtils';
import useApprovalsRealtime from '../../hooks/useApprovalsRealtime';
import Loading from '../Loading';

const EMPTY_PROJECTS = [];

const ApprovalPage = ({ allProjects = false, embedded = false, projects = EMPTY_PROJECTS } = {}) => {
  const [repairRequests, setRepairRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const requestSeqRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryProject = queryParams.get('project');
  const projectIds = useMemo(() => projects.map(project => project.id).filter(Boolean), [projects]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (allProjects) return null;
    if (queryProject) {
      try { localStorage.setItem('selected_project_id', queryProject); } catch {}
      return queryProject;
    }
    try {
      return localStorage.getItem('selected_project_id');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (allProjects) return;
    if (queryProject && queryProject !== selectedProjectId) {
      setSelectedProjectId(queryProject);
      try { localStorage.setItem('selected_project_id', queryProject); } catch {}
    }
  }, [allProjects, queryProject, selectedProjectId]);

  const fetchRepairRequests = useCallback(async (projectId, { silent = false } = {}) => {
    if (!allProjects && !projectId) return;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    try {
      if (!silent) setLoading(true);
      if (allProjects) {
        if (!projectIds.length) {
          setRepairRequests([]);
          setError('');
          setLoading(false);
          return;
        }
        const allowedProjectIds = new Set(projectIds.map(id => String(id)));
        const response = await api.get('/repairs/approvals/');
        if (requestSeqRef.current !== requestSeq) return;
        const items = Array.isArray(response.data) ? response.data : [];
        setRepairRequests(items.filter(item => allowedProjectIds.has(String(item.project?.id || item.project_id))));
      } else {
        const resp = await api.get('/repairs/approvals/', {
          params: { project_id: projectId },
        });
        if (requestSeqRef.current !== requestSeq) return;
        setRepairRequests(resp.data);
      }
      setError('');
    } catch (err) {
      console.error('Ошибка в fetchRepairRequests:', err);
      if (!silent) {
        setError(err.response?.data?.error || 'Ошибка при загрузке списка партий для согласования');
      }
    } finally {
      if (!silent && !allProjects) setLoading(false);
    }
  }, [allProjects, projectIds]);

  useEffect(() => {
    if (allProjects) {
      fetchRepairRequests(null);
      return;
    }
    if (!selectedProjectId) {
      if (!embedded) navigate('/select-project', { state: { from: '/approvals' } });
      return;
    }
    fetchRepairRequests(selectedProjectId);
  }, [allProjects, embedded, selectedProjectId, navigate, fetchRepairRequests]);

  useEffect(() => {
    const requestParam = queryParams.get('request');
    if (!requestParam || !repairRequests.length) return;
    const normalized = requestParam.trim().toLowerCase();
    if (!normalized) return;
    const matched = repairRequests.find(
      (rr) =>
        rr.request_number?.toLowerCase() === normalized ||
        String(rr.id) === normalized
    );
    if (!matched) return;
    setSelectedRequestId((prev) => (prev === matched.id ? prev : matched.id));
    setShowDetailPanel(true);
  }, [queryParams, repairRequests]);

  const handleRowClick = (repairRequest) => {
    setSelectedRequestId(repairRequest.id);
    setShowDetailPanel(true);
  };

  const handleClosePanel = useCallback(() => {
    setShowDetailPanel(false);
    setSelectedRequestId(null);
  }, []);

  const refreshList = useCallback(() => {
    if (allProjects) {
      fetchRepairRequests(null, { silent: true });
    } else if (selectedProjectId) {
      fetchRepairRequests(selectedProjectId, { silent: true });
    }
  }, [allProjects, selectedProjectId, fetchRepairRequests]);

  const handleRealtimeMessage = useCallback((message) => {
    if (!message || !message.action) return;
    if (!allProjects && (
      selectedProjectId &&
      message.project_id &&
      message.project_id.toString() !== selectedProjectId.toString()
    )) {
      return;
    }
    refreshList();
    if (
      showDetailPanel &&
      selectedRequestId &&
      Number(message.repair_request_id) === Number(selectedRequestId) &&
      (message.action === 'approval_state_deleted' || message.is_completed)
    ) {
      handleClosePanel();
    }
  }, [allProjects, refreshList, selectedProjectId, selectedRequestId, showDetailPanel, handleClosePanel]);

  useApprovalsRealtime(allProjects ? null : selectedProjectId, handleRealtimeMessage);

  useEffect(() => {
    if (!showDetailPanel || !selectedRequestId) return;
    const stillExists = repairRequests.some(
      (rr) => Number(rr.id) === Number(selectedRequestId)
    );
    if (!stillExists) {
      handleClosePanel();
    }
  }, [repairRequests, selectedRequestId, showDetailPanel, handleClosePanel]);

  const filteredRepairRequests = repairRequests.filter(
    (rr) =>
      rr.request_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rr.customer?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rr.project?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !embedded) return <Loading message="Загрузка согласований..." />;
  if (error) return <div className={commonStyles.error}>{error}</div>;

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      <div className={commonStyles.pageHeader}>
        <div className={styles.titleGroup}>
          <CheckCircle className={styles.titleIcon} />
          <h1 className={commonStyles.pageTitle}>
            {allProjects ? 'Согласования по всем проектам' : 'Согласование'}
          </h1>
        </div>
        <div className={commonStyles.searchContainer}>
          <Search className={commonStyles.searchIcon} />
          <input
            type="text"
            placeholder="Поиск по номеру заявки..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={commonStyles.searchInput}
          />
        </div>
      </div>

      {loading && embedded && (
        <div className={styles.inlineLoading}>Загрузка согласований...</div>
      )}

      {/* Detail Panel */}
      {showDetailPanel && selectedRequestId && (
        <DetailPanel
          isOpen={showDetailPanel}
          onClose={handleClosePanel}
          title="Детали согласования"
        >
          <ApprovalDetailPage repairRequestId={selectedRequestId} />
        </DetailPanel>
      )}

      <div className={styles.tableWrapper}>
        <table className={commonStyles.table}>
          <thead>
            <tr>
              <th>Номер заявки</th>
              <th>Заказчик</th>
              <th>Проект</th>
              <th>Клиентский доступ</th>
              <th>Статус перемещения</th>
              <th>Прогресс</th>
              <th>Дата создания</th>
            </tr>
          </thead>
          <tbody>
            {filteredRepairRequests.map((rr) => (
              <tr
                key={rr.id}
                onClick={() => handleRowClick(rr)}
                className={`${commonStyles.tableRow} ${styles.clickableRow}`}
              >
                <td>
                  <span className={styles.requestNumber}>{rr.request_number}</span>
                </td>
                <td>{rr.customer?.name || '-'}</td>
                <td>{rr.project?.name || '-'}</td>
                <td>
                  {rr.guest_recipients?.length ? (
                    <span className={styles.guestRecipients}>
                      Отправлено: {rr.guest_recipients.map(item => item.name || item.username).join(', ')}
                    </span>
                  ) : (
                    <span className={styles.noGuestRecipients}>Гость не назначен</span>
                  )}
                </td>
                <td>{rr.location_status_display || '-'}</td>
                <td>
                  {rr.approval_progress
                    ? `${rr.approval_progress.completed} согласовано, ${rr.approval_progress.rejected} отклонено, ${rr.approval_progress.pending} ожидает, ${rr.approval_progress.in_progress} в процессе из ${rr.approval_progress.total}`
                    : '-'}
                </td>
                <td>
                  <span className={styles.date}>
                    {formatDateTime(rr.created_at)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApprovalPage;
